import { call, put, takeLatest, takeEvery, select, delay, cancel } from 'redux-saga/effects';
import {
  GET_CONVERSATION_LIST,
  GET_CONVERSATION_DETAIL,
  SEND_CONVERSATION_MESSAGES,
  START_BOOK_MEETING,
  CONVERSATION_LIST_TYPES,
  GET_INBOX_DETAIL,
  GET_TEAM_MEMBERS,
  GET_CONVERSATION_MEMBERS,
  SET_ARCHIVE_STAR_CONVERSATION,
  ADD_NEW_MEMBER_CONVERSATION,
  REMOVE_MEMBER_CONVERSATION,
  READ_CONVERSATION,
  UPDATE_CONVERSATION_STATUS,
  UPDATE_INBOX_DATA,
  GET_USER_STATUS,
  UPDATE_USER_STATUS,
  GET_CONVERSATION_FROM_PARAMS,
  CLOSE_SELECTED_CONVERSATION,
  SET_CONVERSATION_REQUEST,
  ADD_TO_CONVERSATIONS_LIST,
  REMOVE_FROM_CONVERSATIONS_LIST,
  HANDLE_SENDING_MESSAGE_TO_PUSHER,
  REMOVE_FROM_SENDING_MESSAGES,
  UPDATE_CONVERSATION_LIST_TYPE,
  POLL_BACKEND,
  GET_CANNED_MESSAGES,
  SAVE_CANNED_MESSAGE,
  DELETE_CANNED_MESSAGE,
  SEND_MESSAGE,
  HAS_SEND_MESSAGE,
  CREATE_ZENDESK_TICKET,
  GET_KNOWLEDGE_BASE_ARTICLES,
  GET_CONNECTED_APPS,
  LOGOUT_USER,
  CREATE_SERVICE_CLOUD_CASE,
  GET_SERVICE_CLOUD_ARTICLES,
  BLOCK_VISITOR,
  GET_TAGS,
  GET_SUBSCRIPTION_DETAILS,
  GET_HIGHLIGHTS_FIELDS,
  GET_HIGHLIGHTS_TEMPLATES,
  SAVE_HIGHLIGHTS_BUILDER,
  BLOCKED_TAG,
  GET_ATTACHMENTS,
  UPDATE_UPLOAD_PROGRESS,
} from './constants';
import {
  closeSelectedConversation,
  getInboxDetail,
  getConversationList,
  getConversationDetail,
  updateConversationMessages,
  getConversationListFiltered,
  getTeamMembers,
  getConversationMembers,
  updateConversationList,
  addNewMemberConversation,
  getUserStatus,
  updateUserStatus,
  loadingNewConversation,
  getConversationFromParams,
  setConversationRequest,
  changeCurrentConversation,
  updateSendingMessages,
  handleMessageSendingToPusher,
  removeFromSendingMessages,
  updateLastVisitorReadTime,
  updateLastVisitorDeliveredTime,
  isUpdateConversationLists,
  updateConversationDetails,
  getCannedMessages,
  saveCannedMessage,
  deleteCannedMessage,
  setNewMessages,
  hasSendMessage,
  createZendeskTicket,
  getKnowledgeBaseArticles,
  getConnectedApps,
  logoutUser,
  updateInboxStatistics,
  updateConversationsRead,
  updateSelectedFilters,
  createServiceCloudCase,
  getServiceCloudArticles,
  blockVisitor,
  openVisitorBlockModal,
  getTags,
  getSubscriptionDetails,
  getHighlightsFields,
  getHighlightsTemplates,
  saveHighlightsBuilder,
  getAttachments,
  updateUploadProgress,
} from './actions';
import {
  makeSelectConversationDetailMessages,
  makeSelectConversationListVisitors,
  makeSelectConversationListBot,
  makeSelectConversationDetailConversationId,
  makeSelectConversationParticipants,
  makeSelectCurrentConversation,
  makeSelectInboxSender,
  makeSelectSendingMessages,
  makeSelectInboxPusherChannels,
  makeSelectCannedMessagesList,
  makeSelectCustomFilter,
  makeSelectInboxStatistics,
  makeSelectConversationsRead,
  makeSelectIsVisitorBlocked,
} from './selectors';

import {
  makeSelectNetworkStatus,
  makeSelectIsProfileCompleted,
} from 'ui/containers/ProjectDetailsPage/selectors';
import { PUSHER_EVENT_TYPES } from 'common/constants';
import { LOGOUT_PAGE } from 'common/strings';

import triggerNotification from 'utils/triggerNotification';
import { NOTIFICATION } from 'common/constants';
import request from 'utils/request';
import convertErrorMsg from 'utils/convertErrorMsg';
import { CHAT, ZENDESK_INBOX, SERVICE_CLOUD } from 'common/strings';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { generateUnixTimestamp } from './utils';
import { readToken } from 'utils/readToken';
import { generateHighlightMenuList, generateFilesSchema } from './utils';
import generateRandomId from 'utils/generateRandomId';

export function* conversationRequest({ payload, metadata }) {
  const token = readToken();
  const { conversationId, data } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/accept',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });

    if (payload.accept) {
      yield call(
        updateConversationLists,
        conversationId,
        data,
        UPDATE_CONVERSATION_LIST_TYPE.KNOWN_USER,
        CONVERSATION_LIST_TYPES.NON_BOT
      );
      yield call(getInboxDetails, { metadata: {} });
    } else {
      //
    }

    yield put(setConversationRequest.success());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(setConversationRequest.error(errorMessage));
  }
}

export function* getInboxDetails({ metadata }) {
  const token = readToken();
  const { participant, type, filterParam, activeTab } = metadata;
  const customFilter = yield select(makeSelectCustomFilter());
  const filter = customFilter[activeTab] ? customFilter[activeTab] : {};
  const { fromTime, toTime } = generateUnixTimestamp(filter);
  let inboxFilterParams = fromTime && toTime ? `&fromTime=${fromTime}&toTime=${toTime}` : '';
  if (participant && type && filterParam) {
    inboxFilterParams = `${inboxFilterParams}&participant=${participant}&${filterParam}`;
  }
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/inbox/details?indetails=true` + inboxFilterParams,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data } = response;
    if (participant && type && filterParam) {
      const inboxStatistics = yield select(makeSelectInboxStatistics());
      const statistics = cloneDeep(inboxStatistics);
      statistics[participant][type] = data.statistics[participant][type];
      yield put(updateInboxStatistics.success({ payload: { statistics } }));
    } else {
      yield put(getInboxDetail.success({ data }));
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getInboxDetail.error(errorMessage));
  }
}

export function* getConversationLists({ metadata }) {
  const token = readToken();
  const { type, before, options } = metadata;
  let getParams = '';
  if (
    type === CONVERSATION_LIST_TYPES.BOT ||
    type === CONVERSATION_LIST_TYPES.NON_BOT ||
    type === CONVERSATION_LIST_TYPES.ME
  ) {
    getParams += 'participant=' + type;
  }
  if (before) {
    getParams += '&before=' + before;
  }
  if (options && options.length > 0) {
    getParams += '&' + options;
  }
  if (getParams.length > 0) {
    getParams = '?' + getParams;
  }

  if (!before) {
    yield put(getConversationListFiltered.start());
  }

  try {
    let response = yield call(request, {
      method: 'get',
      endpoint: 'app/inbox/conversations' + getParams,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    response.data.type = type;

    if (before) {
      let list = [];
      if (type === CONVERSATION_LIST_TYPES.NON_BOT || type === CONVERSATION_LIST_TYPES.ME)
        list = yield select(makeSelectConversationListVisitors());
      else if (type === CONVERSATION_LIST_TYPES.BOT)
        list = yield select(makeSelectConversationListBot());

      let uniqConvos = list.map(list => list.id);
      response.data.conversations.forEach(convo => {
        if (!uniqConvos.includes(convo.id)) {
          list = list.concat(convo);
        }
      });
      response.data.conversations = [...list];
    }

    yield put(getConversationList.success({ data: response.data }));

    yield put(getConversationListFiltered.success());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getConversationList.error(errorMessage));
    yield put(getConversationListFiltered.error());
  }
}

export function* getConversationDetails({ metadata }) {
  const token = readToken();

  const currentConversation = yield select(makeSelectCurrentConversation());

  if (
    !currentConversation ||
    !currentConversation.id ||
    !currentConversation.user ||
    !currentConversation.user.id
  ) {
    return;
  }
  const conversationId = currentConversation.id;
  const visitorId = currentConversation.user.id;

  const { lastMessagePos, checkSendingMessages } = metadata;

  let getParams = '';

  if (lastMessagePos) {
    getParams = '?lastMessagePos=' + lastMessagePos;
  } else {
    yield put(loadingNewConversation());
  }
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/inbox/conversations/' + conversationId + '/' + visitorId + getParams,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const currentConversationId = yield select(makeSelectConversationDetailConversationId());
    if (currentConversationId === response.data.conversationId && getParams.length > 0) {
      const currentMessages = yield select(makeSelectConversationDetailMessages());
      response.data.messages = response.data.messages.concat(currentMessages);
    }
    const isVisitorBlocked = response.data?.visitor?.blocked || false;
    yield put(getConversationDetail.success({ data: response.data }));
    yield put(blockVisitor.success({ data: { isBlocked: isVisitorBlocked } }));

    if (checkSendingMessages) {
      checkSendingMessages();
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getConversationDetail.error(errorMessage));
  }
}

export function* getConversationFromparamsStart({ metadata }) {
  const token = readToken();
  const { visitorId, conversationId } = metadata;

  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/inbox/conversations/' + conversationId + '/' + visitorId,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    const messagesToIterate = response.data.messages.slice(0).reverse();
    let message = {
      text: '',
      time: Date.now() / 1000,
    };

    for (let i = 0, len = messagesToIterate.length; i < len; i++) {
      if (messagesToIterate[i].text) {
        message = { ...messagesToIterate[i] };
        break;
      }
    }

    let currentConversation = {
      archived: response.data.details.archived,
      botConv: false,
      id: response.data.conversationId,
      starred: response.data.details.starred,
      status: response.data.details.status,
      unread: response.data.details.unread,
      message: message,
      lastEventTime: message.time,
      account: {},
      user: {
        id: response.data.visitor.userId,
        name: response.data.visitor.name,
        online: response.data.visitor.online,
        location: response.data.visitor.location,
      },
    };
    currentConversation.account = response.data.account || {};

    yield put(
      changeCurrentConversation({
        metadata: { currentConversation: currentConversation },
      })
    );

    yield put(getConversationFromParams.success());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getConversationFromParams.error(errorMessage));
  }
}

export function* sendMessages({ metadata }) {
  const { newMessages, pusher, currentConversation } = metadata;
  const isVisitorBlocked = yield select(makeSelectIsVisitorBlocked());

  let data = { ...currentConversation };
  data.messages = [...newMessages];
  data.updateVisitorOnline = true;
  delete data.message;

  if (!isVisitorBlocked) {
    let sendingMessages = yield select(makeSelectSendingMessages());
    sendingMessages = sendingMessages.concat(newMessages);
    yield put(updateSendingMessages({ data: { messages: sendingMessages } }));
    const networkStatus = yield select(makeSelectNetworkStatus());
    if (networkStatus === 'online') {
      yield put(handleMessageSendingToPusher({ metadata: { pusher } }));
    }

    yield call(
      updateConversationLists,
      data.id,
      data,
      UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE,
      CONVERSATION_LIST_TYPES.NON_BOT,
      null
    );
  } else {
    // isBlocked key is used to show unblock text in chat message
    data.messages = [...data.messages, { isBlocked: true }];
    yield call(updateMessages, {
      metadata: { newMessages: data.messages },
    });
  }
}

export function* removeFromSendingMessagesStart({ metadata }) {
  const { message } = metadata;

  const sendingMessages = [...(yield select(makeSelectSendingMessages()))];

  const updatedSendingMessages = sendingMessages.filter(
    msg => message.time !== msg.time && message.userId === msg.userId
  );

  yield put(updateSendingMessages({ data: { messages: updatedSendingMessages } }));

  let messages = [...(yield select(makeSelectConversationDetailMessages()))];
  let currentConversation = yield select(makeSelectCurrentConversation());

  messages.push(message);
  const data = {
    messages: messages,
  };

  if (message.id === currentConversation.id) {
    // only update conversation if user is in current conversation.
    yield put(updateConversationMessages.success({ data }));
  }
}

function* updateMessages({ metadata }) {
  const { newMessages, eventType } = metadata;

  const sendingMessages = yield select(makeSelectSendingMessages());

  let updatedSendingMessages = [];

  sendingMessages.forEach(message => {
    const isMessageSend = newMessages.find(msg => {
      if (msg.userId === message.userId && msg.text && msg.text === message.text) return true;
      else return false;
    });
    if (!isMessageSend) {
      updatedSendingMessages.push(message);
    }
  });

  let messages = [...(yield select(makeSelectConversationDetailMessages()))];

  let feedbackNew = true;

  if (eventType === PUSHER_EVENT_TYPES.MESSAGE_UPDATE) {
    const targetMessage = newMessages[0];
    if (targetMessage && 'feedback' in targetMessage) {
      const feedbackId = targetMessage['feedback'].id || targetMessage._id;
      messages = messages.map(message => {
        if ('feedback' in message) {
          const messageFeedbackId = message.feedback.id || message._id;
          if (messageFeedbackId === feedbackId) {
            feedbackNew = false;
            return {
              ...targetMessage,
            };
          }
        }
        return message;
      });
    } else if (targetMessage && 'calendar' in targetMessage) {
      const calendarId = newMessages[0]._id;
      messages = messages.map(message => {
        if ('calendar' in message) {
          const messageCalendarId = message._id;
          if (messageCalendarId === calendarId) {
            feedbackNew = false;
            return {
              ...targetMessage,
            };
          }
        }
        return message;
      });
    }
  }

  if (feedbackNew) {
    messages = messages.concat(newMessages);
  }

  const data = {
    messages: messages,
  };
  yield put(updateSendingMessages({ data: { messages: updatedSendingMessages } }));
  yield put(updateConversationMessages.success({ data }));
}

export function* startBooking({ metadata }) {
  const token = readToken();
  const { conversationId, visitorId } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/' + visitorId + '/bookmeeting',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
  }
}

export function* getAgents() {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/member/search',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const newUsers = response.data.users.map(agent => {
      agent.display = agent.name;
      agent.avatarUrl = agent.img;
      agent.nickname = agent.name;
      return agent;
    });
    response.data.users = newUsers;

    yield put(getTeamMembers.success({ data: response.data }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getTeamMembers.error(errorMessage));
  }
}

export function* getParticipants({ metadata }) {
  const token = readToken();
  const { conversationId, activeTab, setActiveTab, setUpdateConversationsList } = metadata;
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/inbox/conversations/' + conversationId + '/members',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data } = response || {};
    const { bot } = data;
    if (bot === true && activeTab !== 1 && setActiveTab && setUpdateConversationsList) {
      setActiveTab(1);
      setUpdateConversationsList(true);
    } else if (bot === false && activeTab !== 0 && setActiveTab && setUpdateConversationsList) {
      setActiveTab(0);
      setUpdateConversationsList(true);
    }
    yield put(getConversationMembers.success({ data }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getConversationMembers.error(errorMessage));
  }
}

function changeParticularConversation(conversation, conversationList) {
  return [
    ...conversationList.map(convo => {
      if (convo.id === conversation.id) {
        return conversation;
      } else return convo;
    }),
  ];
}

function moveConversationToTop(conversation, conversationList) {
  const list = [...conversationList.filter(convo => convo.id !== conversation.id)];
  list.unshift({ ...conversation });
  return list;
}

function* handleInboxStatistics({
  checkUserList,
  conversationId,
  isUserOnline,
  isStarred,
  updateVisitorOnline,
}) {
  let isNewConv = true;
  if (checkUserList) {
    const userConversationList = yield select(makeSelectConversationListVisitors());
    const conversation = {
      ...userConversationList.find(conversation => conversationId === conversation.id),
    };
    isNewConv = isEmpty(conversation) ? true : false;
  }
  if (isNewConv) {
    yield put(
      updateSelectedFilters.success({
        payload: { selectedFilters: { bot: [], nonBot: [] } },
      })
    );
    const inboxStatistics = yield select(makeSelectInboxStatistics());
    const statistics = cloneDeep(inboxStatistics);
    if (isStarred) {
      statistics.nonBot.starred += 1;
      if (statistics.bot.starred > 0) {
        statistics.bot.starred -= 1;
      }
    }
    if (isUserOnline && updateVisitorOnline) {
      statistics.nonBot.onlineVisitor += 1;
      if (statistics.bot.onlineVisitor > 0) {
        statistics.bot.onlineVisitor -= 1;
      }
    }
    yield put(updateInboxStatistics.success({ payload: { statistics } }));
  }
}

function* updateConversationLists(conversationId, value, type, conversationType, data) {
  let list = [];
  if (conversationType === CONVERSATION_LIST_TYPES.BOT)
    list = yield select(makeSelectConversationListBot());
  else if (
    conversationType === CONVERSATION_LIST_TYPES.NON_BOT ||
    conversationType === CONVERSATION_LIST_TYPES.ME
  )
    list = yield select(makeSelectConversationListVisitors());

  const agent = yield select(makeSelectInboxSender());

  let currentConversation = { ...(yield select(makeSelectCurrentConversation())) };
  let visitorData;
  if (
    type === UPDATE_CONVERSATION_LIST_TYPE.KNOWN_USER ||
    type === UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE
  ) {
    visitorData = {
      account: value.account,
      user: value.user,
      id: value.id,
      status: value.status || 'open',
      botConv: value.botConv || false,
      mentions: [],
      message: {
        text: '',
        time: Date.now() / 1000,
      },
      members: value.members,
      lastEventTime: Date.now() / 1000,
      archived: value.usersArchived ? value.usersArchived.includes(agent.userId) : false,
      starred: value.usersStarred ? value.usersStarred.includes(agent.userId) : false,
    };
    if (value.messages) {
      for (let i = value.messages.length - 1; i >= 0; i--) {
        if (value.messages[i].text) {
          visitorData.message.text = value.messages[i].text;
          visitorData.message.time = value.messages[i].time;
          break;
        }
      }
    }
  }

  let targetConversation = {
    ...list.find(conversation => conversationId === conversation.id),
  };
  if (type === UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE && isEmpty(targetConversation)) {
    yield call(handleInboxStatistics, {
      checkUserList: false,
      conversationId,
      isStarred: value.starred,
      isUserOnline: value.user.online,
      updateVisitorOnline: value.updateVisitorOnline,
    });
  }
  if (targetConversation && targetConversation.id && data && data.lastMessage) {
    if (Array.isArray(data.lastMessage)) {
      targetConversation.message = data.lastMessage[0];
    } else {
      targetConversation.message = data.lastMessage;
    }
  }
  let updatedConversationList = [];
  updatedConversationList = { ...updatedConversationList };

  switch (type) {
    case UPDATE_CONVERSATION_LIST_TYPE.ARCHIVE:
      if (currentConversation.id === conversationId) {
        currentConversation.archived = value;
        yield put(
          changeCurrentConversation({
            metadata: { currentConversation: { ...currentConversation } },
          })
        );
      }
      if (targetConversation && targetConversation.id) {
        targetConversation.archived = value;
        updatedConversationList = changeParticularConversation(targetConversation, list);
      } else {
        if (currentConversation.id === conversationId) {
          if (data && data.lastMessage) {
            if (Array.isArray(data.lastMessage)) {
              currentConversation.message = data.lastMessage[0];
            } else {
              currentConversation.message = data.lastMessage;
            }
          }
          updatedConversationList = moveConversationToTop({ ...currentConversation }, list);
        }
      }

      break;
    case UPDATE_CONVERSATION_LIST_TYPE.STAR:
      if (currentConversation.id === conversationId) {
        currentConversation.starred = value;
        if (data && data.lastMessage) {
          if (Array.isArray(data.lastMessage)) {
            currentConversation.message = data.lastMessage[0];
          } else {
            currentConversation.message = data.lastMessage;
          }
        }
        yield put(
          changeCurrentConversation({
            metadata: { currentConversation: { ...currentConversation } },
          })
        );
      }
      if (targetConversation && targetConversation.id) {
        targetConversation.starred = value;
        updatedConversationList = changeParticularConversation(targetConversation, list);
      } else {
        if (currentConversation.id === conversationId) {
          updatedConversationList = moveConversationToTop({ ...currentConversation }, list);
        }
      }
      break;
    case UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE:
      if (targetConversation && targetConversation.id) {
        for (let i = value.messages.length - 1; i >= 0; i--) {
          if (value.messages[i].text) {
            targetConversation.message = value.messages[i];
            break;
          }
        }
        visitorData = {
          ...targetConversation,
          lastEventTime: Math.floor(Date.now() / 1000),
        };
      }
      if (currentConversation.id !== visitorData.id) {
        visitorData.unread = true;
      }
      updatedConversationList = moveConversationToTop(visitorData, list);

      break;
    case UPDATE_CONVERSATION_LIST_TYPE.READ:
      if (targetConversation && targetConversation.id) {
        targetConversation.unread = value;
        updatedConversationList = changeParticularConversation(targetConversation, list);
      }

      break;
    case UPDATE_CONVERSATION_LIST_TYPE.STATUS:
      if (currentConversation.id === conversationId) {
        currentConversation.status = value;
        if (data && data.lastMessage) {
          if (Array.isArray(data.lastMessage)) {
            currentConversation.message = data.lastMessage[0];
          } else {
            currentConversation.message = data.lastMessage;
          }
        }
        yield put(
          changeCurrentConversation({
            metadata: { currentConversation: { ...currentConversation } },
          })
        );
      }
      if (targetConversation && targetConversation.id) {
        targetConversation.status = value;
        updatedConversationList = changeParticularConversation(targetConversation, list);
      } else {
        if (currentConversation.id === conversationId) {
          updatedConversationList = moveConversationToTop({ ...currentConversation }, list);
        }
      }
      break;
    case UPDATE_CONVERSATION_LIST_TYPE.KNOWN_USER:
      if (targetConversation && targetConversation.id) visitorData = { ...targetConversation };

      updatedConversationList = moveConversationToTop(visitorData, list);
      yield put(
        changeCurrentConversation({
          metadata: { currentConversation: visitorData },
        })
      );
      break;
    case UPDATE_CONVERSATION_LIST_TYPE.STATUS_CHANGE:
      updatedConversationList = [
        ...list.map(convo => {
          const temp = cloneDeep(convo);
          if (temp.user && temp.user.id === value.user.id) {
            temp.user.online = value.user.online;
          }
          return temp;
        }),
      ];
      break;
    case UPDATE_CONVERSATION_LIST_TYPE.VISITOR_BLOCKED:
      if (currentConversation.id === conversationId) {
        currentConversation = cloneDeep(currentConversation);
        currentConversation.user.blocked = value;
        if (data && data.lastMessage) {
          if (Array.isArray(data.lastMessage)) {
            currentConversation.message = data.lastMessage[0];
          } else {
            currentConversation.message = data.lastMessage;
          }
        }
        yield put(
          changeCurrentConversation({
            metadata: { currentConversation: { ...currentConversation } },
          })
        );
      }
      if (targetConversation && targetConversation.id) {
        targetConversation = {
          ...targetConversation,
          user: { ...targetConversation.user, blocked: value },
        };
        updatedConversationList = changeParticularConversation(targetConversation, list);
      } else {
        if (currentConversation.id === conversationId) {
          updatedConversationList = moveConversationToTop({ ...currentConversation }, list);
        }
      }
      break;
    case UPDATE_CONVERSATION_LIST_TYPE.UPDATE_UPLOAD_PROGRESS:
      updatedConversationList = list.map(conversation => {
        if (conversation.id === conversationId) {
          return { ...conversation, attachmentProgress: value };
        } else {
          return conversation;
        }
      });
      break;
    default:
      break;
  }

  let updatedList = {};
  updatedList.list = updatedConversationList;
  updatedList.type = conversationType;

  if (updatedList.list.length) yield put(updateConversationList({ data: updatedList }));
}

export function* addToConversationList({ metadata }) {
  const { conversationType, conversation } = metadata;
  let list = [];
  if (conversationType === CONVERSATION_LIST_TYPES.BOT)
    list = yield select(makeSelectConversationListBot());
  else if (
    conversationType === CONVERSATION_LIST_TYPES.NON_BOT ||
    conversationType === CONVERSATION_LIST_TYPES.ME
  )
    list = yield select(makeSelectConversationListVisitors());

  const findConversation = list.find(convo => convo.id === conversation.id);
  if (!findConversation) {
    list.unshift(conversation);
    let updatedList = {};
    updatedList.list = [...list];
    updatedList.type = conversationType;
    if (updatedList.list.length) yield put(updateConversationList({ data: updatedList }));
  }
}

export function* removeFromConversationList({ metadata }) {
  const { conversationType, conversation } = metadata;
  let list = [];
  if (conversationType === CONVERSATION_LIST_TYPES.BOT)
    list = yield select(makeSelectConversationListBot());
  else if (
    conversationType === CONVERSATION_LIST_TYPES.NON_BOT ||
    conversationType === CONVERSATION_LIST_TYPES.ME
  )
    list = yield select(makeSelectConversationListVisitors());

  const findConversation = list.find(convo => convo.id === conversation.id);
  if (findConversation) {
    const newList = list.filter(convo => convo.id !== conversation.id);
    let updatedList = {};
    updatedList.list = [...newList];
    updatedList.type = conversationType;
    if (updatedList.list.length) yield put(updateConversationList({ data: updatedList }));
  }
}

export function* setArchiveStarConversation({ payload, metadata }) {
  const token = readToken();
  const { conversationId, type, conversationType } = metadata;
  const { starred } = payload;
  try {
    let currentConversation = { ...(yield select(makeSelectCurrentConversation())) };
    if (type === UPDATE_CONVERSATION_LIST_TYPE.ARCHIVE) {
      yield call(updateConversationLists, conversationId, payload.archived, type, conversationType);
      currentConversation.archived = payload.archived;
      yield put(
        changeCurrentConversation({
          metadata: { currentConversation: { ...currentConversation } },
        })
      );
    } else if (type === UPDATE_CONVERSATION_LIST_TYPE.STAR) {
      const inboxStatistics = yield select(makeSelectInboxStatistics());
      const statistics = cloneDeep(inboxStatistics);
      if (starred) {
        statistics[conversationType].starred += 1;
      } else if (statistics[conversationType].starred > 0) {
        statistics[conversationType].starred -= 1;
      }
      yield put(updateInboxStatistics.success({ payload: { statistics } }));
      yield call(updateConversationLists, conversationId, starred, type, conversationType);
      currentConversation.starred = starred;
      yield put(
        changeCurrentConversation({
          metadata: { currentConversation: { ...currentConversation } },
        })
      );
    }

    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/' + type,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
  }
}

export function* addNewMember({ payload, metadata }) {
  const token = readToken();
  const { conversationId } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/members',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });

    triggerNotification({
      title: CHAT.ALERTS.AGENTS_ADDED_SUCCESS,
      type: NOTIFICATION.TYPE.SUCCESS,
    });

    yield put(addNewMemberConversation.success());
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(addNewMemberConversation.error());
  }
}

export function* removeMember({ payload, metadata }) {
  const token = readToken();
  const { conversationId } = metadata;
  let participants = yield select(makeSelectConversationParticipants());
  try {
    let newData = {};
    if (payload.appUserId) {
      newData.appUsers = participants.appUsers.filter(agent => agent.id !== payload.appUserId);
      newData.slackUsers = participants.slackUsers;
    } else if (payload.slackThreadId) {
      newData.appUsers = participants.appUsers;
      newData.slackUsers = participants.slackUsers.filter(
        agent => agent.threadId !== payload.slackThreadId
      );
    }
    yield put(getConversationMembers.success({ data: newData }));

    yield call(request, {
      method: 'delete',
      endpoint: 'app/inbox/conversations/' + conversationId + '/members',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });

    triggerNotification({
      title: CHAT.ALERTS.REMOVE_AGENT_SUCCESS,
      type: NOTIFICATION.TYPE.SUCCESS,
    });
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(getConversationMembers.success({ data: participants }));
  }
}

export function* readConversation({ metadata }) {
  const token = readToken();
  const { conversationId, conversationType } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/read',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {},
      },
    });
    if (conversationType) {
      const inboxStatistics = yield select(makeSelectInboxStatistics());
      const statistics = cloneDeep(inboxStatistics);
      if (statistics[conversationType].unread > 0) {
        statistics[conversationType].unread -= 1;
      }
      yield put(updateInboxStatistics.success({ payload: { statistics } }));
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
  }
}

export function* updateStatus({ payload, metadata }) {
  const token = readToken();
  const { conversationId } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/' + conversationId + '/status',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
  }
}

export function* updateOnlineVisitorCount({ count }) {
  const { bot = 0, nonBot = 0 } = count || {};
  const inboxStatistics = yield select(makeSelectInboxStatistics());
  const statistics = cloneDeep(inboxStatistics);
  statistics.bot.onlineVisitor += bot;
  statistics.nonBot.onlineVisitor += nonBot;
  yield put(updateInboxStatistics.success({ payload: { statistics } }));
}

export function* updateData({ metadata }) {
  const { data, switchToUserTab } = metadata;
  let agent = undefined;
  const {
    id: conversationId,
    previousStatus,
    status,
    count,
    usersStarred,
    user,
    botConv,
    previousBotConv,
  } = data;
  const currentConversation = yield select(makeSelectCurrentConversation());
  if (data && data.eventType) {
    const conversationType = botConv
      ? CONVERSATION_LIST_TYPES.BOT
      : CONVERSATION_LIST_TYPES.NON_BOT;
    switch (data.eventType) {
      case PUSHER_EVENT_TYPES.VISITOR_STATUS_CHANGE:
        yield call(updateOnlineVisitorCount, { count });
        yield call(
          updateConversationLists,
          null,
          data,
          UPDATE_CONVERSATION_LIST_TYPE.STATUS_CHANGE,
          conversationType
        );
        if (data.id === currentConversation.id) {
          if (data.messages)
            yield call(updateMessages, {
              metadata: { newMessages: data.messages },
            });
        }
        break;

      case PUSHER_EVENT_TYPES.NEW_MESSAGE:
        if (data.messages && data.id === currentConversation.id) {
          yield call(updateMessages, {
            metadata: { newMessages: data.messages, eventType: data.eventType },
          });
          const feedback = data.messages[0];
          if (feedback && 'feedback' in feedback && feedback.provider === 'app') {
            yield put(updateConversationDetails());
          }
        }
        // eslint-disable-next-line no-case-declarations
        const conversationsRead = { ...(yield select(makeSelectConversationsRead())) };
        if (currentConversation.id !== conversationId && conversationsRead[conversationId]) {
          const inboxStatistics = yield select(makeSelectInboxStatistics());
          const statistics = cloneDeep(inboxStatistics);
          statistics[conversationType].unread += 1;
          yield put(updateInboxStatistics.success({ payload: { statistics } }));
          delete conversationsRead[conversationId];
          yield put(updateConversationsRead.success({ payload: { conversationsRead } }));
        }
        yield call(
          updateConversationLists,
          data.id,
          data,
          UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE,
          conversationType
        );
        break;
      case PUSHER_EVENT_TYPES.MESSAGE_UPDATE:
        if (data.messages && data.id === currentConversation.id) {
          yield call(updateMessages, {
            metadata: { newMessages: data.messages, eventType: data.eventType },
          });
        }
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_STATUS_CHANGE:
        agent = yield select(makeSelectInboxSender());
        if (previousStatus && status) {
          const inboxStatistics = yield select(makeSelectInboxStatistics());
          const statistics = cloneDeep(inboxStatistics);
          if (previousBotConv && data.members && data.members.app.includes(agent.userId)) {
            statistics.nonBot.my += 1;
          }
          if (
            botConv === false &&
            previousBotConv === false &&
            statistics[conversationType][previousStatus] > 0
          ) {
            statistics[conversationType][previousStatus] -= 1;
          }
          statistics[conversationType][status] += 1;
          yield put(updateInboxStatistics.success({ payload: { statistics } }));
        }
        yield call(handleInboxStatistics, {
          checkUserList: true,
          conversationId,
          isUserOnline: user.online,
          isStarred: usersStarred.includes(agent.userId),
          updateVisitorOnline: true,
        });
        yield call(
          updateConversationLists,
          data.id,
          data.status,
          UPDATE_CONVERSATION_LIST_TYPE.STATUS,
          conversationType,
          data
        );
        if (data.id === currentConversation.id) {
          if (data.messages)
            yield call(updateMessages, {
              metadata: { newMessages: data.messages },
            });
          switchToUserTab();
        }
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_MEMBER_JOIN:
        agent = yield select(makeSelectInboxSender());
        if (data.members && data.members.joined) {
          if (data.members.joined.includes(agent.userId)) {
            yield put(
              isUpdateConversationLists({
                data: true,
                type: PUSHER_EVENT_TYPES.CONVERSATION_MEMBER_JOIN,
              })
            );
            const inboxStatistics = yield select(makeSelectInboxStatistics());
            const statistics = cloneDeep(inboxStatistics);
            statistics.nonBot.my += 1;
            yield put(updateInboxStatistics.success({ payload: { statistics } }));
          }
        }
        yield call(handleInboxStatistics, {
          checkUserList: true,
          conversationId,
          isUserOnline: user.online,
          isStarred: usersStarred.includes(agent.userId),
          updateVisitorOnline: true,
        });

        if (data.id === currentConversation.id) {
          yield put(
            getConversationMembers.start({
              metadata: { conversationId: data.id },
            })
          );
          if (data.messages)
            yield call(updateMessages, {
              metadata: { newMessages: data.messages },
            });
          switchToUserTab();
        }

        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_MEMBER_LEAVE:
        agent = yield select(makeSelectInboxSender());
        if (data.members && data.members.left) {
          if (data.members.left.includes('bot')) {
            yield call(
              updateConversationLists,
              data.id,
              data,
              UPDATE_CONVERSATION_LIST_TYPE.UPDATE_LAST_MESSAGE,
              conversationType
            );
          }
          if (data.members.left.includes(agent.userId)) {
            yield put(
              isUpdateConversationLists({
                data: true,
                type: PUSHER_EVENT_TYPES.CONVERSATION_MEMBER_LEAVE,
              })
            );
            const inboxStatistics = yield select(makeSelectInboxStatistics());
            const statistics = cloneDeep(inboxStatistics);
            if (statistics.nonBot.my > 0) {
              statistics.nonBot.my -= 1;
            }
            yield put(updateInboxStatistics.success({ payload: { statistics } }));
          }
        }

        if (data.id === currentConversation.id) {
          if (data.messages)
            yield call(updateMessages, {
              metadata: { newMessages: data.messages },
            });
          yield put(
            getConversationMembers.start({
              metadata: { conversationId: data.id },
            })
          );
        }

        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_READ:
        yield call(
          updateConversationLists,
          data.id,
          false,
          UPDATE_CONVERSATION_LIST_TYPE.READ,
          conversationType,
          data
        );
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_STAR:
        if (data.id === currentConversation.id) {
          yield call(
            updateConversationLists,
            data.id,
            data.starred,
            UPDATE_CONVERSATION_LIST_TYPE.STAR,
            conversationType,
            data
          );
        }
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_ARCHIVE:
        if (data.id === currentConversation.id) {
          yield call(
            updateConversationLists,
            data.id,
            data.archived,
            UPDATE_CONVERSATION_LIST_TYPE.ARCHIVE,
            conversationType,
            data
          );
        }
        break;

      case PUSHER_EVENT_TYPES.APPUSER_AVAILABLE:
        yield put(
          updateUserStatus.success({
            data: { available: data.acceptConversations },
          })
        );
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_VISITOR_READ:
        if (data.id === currentConversation.id) {
          yield put(updateLastVisitorDeliveredTime({ data: Date.now() / 1000 }));
          yield put(updateLastVisitorReadTime({ data: Date.now() / 1000 }));
        }
        break;

      case PUSHER_EVENT_TYPES.CONVERSATION_VISITOR_DELIVERED:
        if (data.id === currentConversation.id) {
          yield put(updateLastVisitorDeliveredTime({ data: Date.now() / 1000 }));
        }
        break;

      case PUSHER_EVENT_TYPES.ONLINE_CONVERSATION_COUNT_UPDATE:
        yield call(updateOnlineVisitorCount, { count });
        break;

      case PUSHER_EVENT_TYPES.VISITOR_BLOCKED_STATUS_CHANGE:
        yield call(
          updateConversationLists,
          data.id,
          user?.blocked,
          UPDATE_CONVERSATION_LIST_TYPE.VISITOR_BLOCKED,
          conversationType,
          data
        );
        if (data.id === currentConversation.id) {
          if (data.messages)
            yield call(updateMessages, {
              metadata: { newMessages: data.messages },
            });
        }
        yield put(blockVisitor.success({ data: { isBlocked: user?.blocked } }));
        break;

      default:
        break;
    }
  }
}

export function* getUserStatusStart() {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/user/available',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    yield put(getUserStatus.success({ data: response.data }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
  }
}

export function* updateUserStatusStart({ payload, metadata }) {
  const token = readToken();

  try {
    const isProfileComplete = yield select(makeSelectIsProfileCompleted());
    if (!isProfileComplete && payload.available) {
      triggerNotification({
        title: CHAT.ALERTS.COMPLETE_PROFILE,
        type: NOTIFICATION.TYPE.ERROR,
      });
    } else {
      yield put(updateUserStatus.success({ data: payload }));
      yield call(request, {
        method: 'post',
        endpoint: 'app/user/available',
        config: {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          data: {
            ...payload,
          },
        },
      });

      triggerNotification({
        title: CHAT.ALERTS.AVAILABLE_STATUS_CHANGE_SUCCESS,
        type: NOTIFICATION.TYPE.SUCCESS,
      });
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    payload.available = !payload.available;
    yield put(updateUserStatus.success({ data: payload }));
  }
}

export function* closeSelectedConversationList({ payload, metadata }) {
  const token = readToken();
  const { setCheckedConversations } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/inbox/conversations/status',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });

    triggerNotification({
      title: CHAT.ALERTS.CLOSE_CONVERSATIONS_SUCCESS,
      type: NOTIFICATION.TYPE.SUCCESS,
    });

    setCheckedConversations([]);
  } catch (error) {
    const errorMessage = convertErrorMsg(error);

    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });

    yield put(closeSelectedConversation.error({ error: error }));
  }
}

function* sendToPusher({ metadata }) {
  const { pusher, channel, data, message } = metadata;
  data.socketId = pusher.connection.socket_id;
  let triggered = pusher.channel(channel).trigger('client-app-message', data);
  if (triggered) {
    if (message) yield put(removeFromSendingMessages({ metadata: { message } }));
  }
}

export function* handleSendingMessageToPusher({ metadata }) {
  const token = readToken();
  const { pusher } = metadata;

  const sendingMessages = yield select(makeSelectSendingMessages());

  const pusherChannels = yield select(makeSelectInboxPusherChannels());

  const currentConversation = yield select(makeSelectCurrentConversation());

  for (let msg of sendingMessages) {
    if (msg.text) {
      let data = {
        senderId: msg.userId,
        channelName: currentConversation.id,
        visitorId: msg.visitorId,
        message: { text: msg.text },
        projectId: window.appDetails.projectId,
        ...(msg.cannedMessage && {
          cannedMessage: msg.cannedMessage,
        }),
        senderSignature: token,
      };
      if (msg.mentions && msg.mentions.length > 0) {
        data.message.mentions = msg.mentions;
      }
      yield call(sendToPusher, {
        metadata: { pusher, channel: pusherChannels[0].id, data, message: msg },
      });
    } else if (msg.feedback) {
      let data = {
        senderId: msg.userId,
        channelName: currentConversation.id,
        visitorId: msg.visitorId,
        feedback: msg.feedback,
        projectId: window.appDetails.projectId,
        senderSignature: token,
      };
      yield call(sendToPusher, {
        metadata: { pusher, channel: pusherChannels[0].id, data, message: msg },
      });
    } else if (msg.attachments) {
      const data = {
        senderId: msg.userId,
        channelName: msg.id,
        visitorId: msg.visitorId,
        attachment: {
          assemblyUrl: msg.assemblyUrl,
        },
        projectId: window.appDetails.projectId,
        senderSignature: token,
      };
      yield call(sendToPusher, {
        metadata: { pusher, channel: pusherChannels[0].id, data, message: msg },
      });
    } else if (msg.gif) {
      const data = {
        senderId: msg.userId,
        channelName: msg.id,
        visitorId: msg.visitorId,
        gif: msg.gif,
        projectId: window.appDetails.projectId,
        senderSignature: token,
      };
      yield call(sendToPusher, {
        metadata: { pusher, channel: pusherChannels[0].id, data, message: msg },
      });
    } else {
      continue;
    }
  }
}

export function* pollBackendStart({ metadata }) {
  const token = readToken();
  try {
    yield call(request, {
      method: 'post',
      endpoint: `app/user/inbox/conversation/active`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...metadata,
        },
      },
    });
  } catch (error) {
    // do nothing
  }
}

export function* getCannedMessagesStart({ metadata }) {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/cannedmessages`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const cannedMessageData = response.data && response.data.cannedMessages;
    yield put(getCannedMessages.success({ data: cannedMessageData }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getCannedMessages.error({ error: errorMessage }));
  }
}

export function* saveCannedMessageStart({ metadata }) {
  const token = readToken();
  const { message, actions } = metadata;
  const cannedMessageData = yield select(makeSelectCannedMessagesList());
  try {
    const response = yield call(request, {
      method: 'post',
      endpoint: `app/cannedmessages`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          message: message,
        },
      },
    });
    const messageId = response.data && response.data.messageId;
    yield put(
      saveCannedMessage.success({
        data: [...cannedMessageData, { messageId: messageId, message: message }],
      })
    );
    actions.clear();
    yield put(setNewMessages({ data: true }));
    yield delay(5000);
    yield put(setNewMessages({ data: false }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(saveCannedMessage.error({ error: errorMessage }));
  }
}

export function* deleteCannedMessageStart({ metadata }) {
  const token = readToken();
  const { messageId, setIsDeleting } = metadata;
  const cannedMessageData = yield select(makeSelectCannedMessagesList());
  try {
    yield call(request, {
      method: 'delete',
      endpoint: `app/cannedmessages/${messageId}`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    yield put(
      deleteCannedMessage.success({
        data: cannedMessageData.filter(message => message.messageId !== messageId),
      })
    );
    setIsDeleting(false);
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(deleteCannedMessage.error({ error: errorMessage }));
  }
}

export function* sendMessageStart({ metadata }) {
  const { handleSend, args } = metadata;
  try {
    yield delay(200);
    yield call(handleSend, args);
    yield put(hasSendMessage.start());
  } catch (error) {
    // do nothing
  }
}

export function* hasSendMessageStart() {
  try {
    yield put(hasSendMessage.success({ data: true }));
    yield delay(3000);
    yield put(hasSendMessage.success({ data: false }));
  } catch (error) {
    // do nothing
  }
}

export function* createZendeskTicketSaga({ payload }) {
  const token = readToken();
  const { hideModal, data: payloadData, updateVisitorDetails, showModal } = payload || {};
  try {
    const response = yield call(request, {
      method: 'post',
      endpoint: 'app/zendesk/ticket',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payloadData,
        },
      },
    });
    const {
      data: { ticketId },
    } = response;
    if (typeof parseInt(ticketId) === 'number') {
      yield put(createZendeskTicket.success({ data: { ticketId } }));
      triggerNotification({
        title: ZENDESK_INBOX.ALERTS.ZENDESK_TICKET_SUCCESS + ticketId,
        type: NOTIFICATION.TYPE.SUCCESS,
      });
    }
    if (hideModal) {
      hideModal();
    }
    if (updateVisitorDetails && showModal) {
      showModal();
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(createZendeskTicket.error({ errorMessage }));
  }
}

export function* getKnowledgeBaseArticlesSaga({ metadata }) {
  const token = readToken();
  let { keyword } = metadata;
  keyword = encodeURIComponent(keyword);
  try {
    if (keyword.length === 0) {
      yield put(getKnowledgeBaseArticles.success({ data: [] }));
      yield cancel();
    }
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/zendesk/knowledgesearch?keyword=${keyword}`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data } = response;
    yield put(getKnowledgeBaseArticles.success({ data }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getKnowledgeBaseArticles.error({ errorMessage }));
  }
}

export function* getConnectedAppsSaga() {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/integration',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const {
      data: { integrations },
    } = response;
    const connectedApps = Object.keys(integrations).filter(key => integrations[key]);
    yield put(getConnectedApps.success({ data: connectedApps }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getConnectedApps.error());
  }
}

export function* logoutUserStart({ metadata }) {
  const token = readToken();
  const { logOut, history, setLogoutProgress } = metadata || {};
  try {
    const response = yield call(request, {
      method: 'post',
      endpoint: 'app/logout',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {},
      },
    });
    if (response.status === 200) {
      triggerNotification({
        title: LOGOUT_PAGE.ALERTS.LOGOUT_SUCCESS,
        type: NOTIFICATION.TYPE.SUCCESS,
      });
      if (setLogoutProgress) setLogoutProgress(false);
      if (logOut) logOut();
      // if (history) history.push('/');
      yield put(logoutUser.success({ data: response.data }));
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(logoutUser.error({ error: error }));
    if (setLogoutProgress) setLogoutProgress(false);
  }
}

export function* getServiceCloudArticlesSaga({ metadata }) {
  const token = readToken();
  let { keyword } = metadata;
  keyword = encodeURIComponent(keyword);
  try {
    if (keyword.length === 0) {
      yield put(getServiceCloudArticles.success({ data: [] }));
      yield cancel();
    }
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/serviceCloud/knowledgesearch?keyword=${keyword}`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data } = response;
    yield put(getServiceCloudArticles.success({ data }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getServiceCloudArticles.error({ errorMessage }));
  }
}

export function* createServiceCloudCaseSaga({ payload }) {
  const token = readToken();
  const { hideModal, data: payloadData, updateVisitorDetails, showModal } = payload || {};
  try {
    const response = yield call(request, {
      method: 'post',
      endpoint: 'app/serviceCloud/ticket',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payloadData,
        },
      },
    });
    const { data: caseId } = response;
    if (typeof parseInt(caseId) === 'number') {
      yield put(createServiceCloudCase.success({ data: { caseId } }));
      triggerNotification({
        title: SERVICE_CLOUD.ALERTS.SERVICE_CLOUD_CASE_CREATION_SUCCESS + caseId,
        type: NOTIFICATION.TYPE.SUCCESS,
      });
      if (hideModal) {
        hideModal();
      }
      if (updateVisitorDetails && showModal) {
        showModal();
      }
    }
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(createServiceCloudCase.error({ errorMessage }));
  }
}

export function* blockVisitorSaga({ metadata, payload }) {
  const token = readToken();
  const { visitorId, visitorName, companyName, addOrRemoveTag } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: `app/inbox/visitor/block/${visitorId}`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });
    yield put(openVisitorBlockModal({ showModal: false }));
    const options = {
      visitorId,
      tagId: BLOCKED_TAG.tagId,
      tagName: BLOCKED_TAG?.tagName || '',
      isChecked: !payload.block,
      type: payload.block ? 'add' : 'remove',
    };
    addOrRemoveTag({ metadata: options });
    const notificationMessage = `${visitorName} ${companyName ? `from ${companyName} ` : ''} is ${
      payload.block ? 'blocked' : 'unblocked'
    }`;
    triggerNotification({
      title: notificationMessage,
      type: NOTIFICATION.TYPE.SUCCESS,
    });
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(blockVisitor.error({ error: errorMessage }));
  }
}

export function* getTagsSaga() {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/tags?all=true',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data = [] } = response || {};
    const tagsList = data.filter(tag => tag.name && tag.name.trim());
    yield put(getTags.success({ data: tagsList }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getTags.error({ error: errorMessage }));
  }
}

export function* getSubscriptionDetailsSaga() {
  try {
    const subscriptions = {
      hasIntent: false,
      hasScoops: false,
      hasTechnologies: false,
    };

    const token = readToken();
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/details/dozi/subscription',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data = {} } = response || {};
    const { doziSubscriptionPlan = {} } = data;
    const { hasIntent = false, hasScoops = false, hasTechnology = false } = doziSubscriptionPlan;
    subscriptions.hasIntent = hasIntent;
    subscriptions.hasScoops = hasScoops;
    subscriptions.hasTechnologies = hasTechnology;

    yield put(getSubscriptionDetails.success({ data: subscriptions }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getSubscriptionDetails.error({ error }));
  }
}

export function* getHighlightsFieldsSaga({ metadata }) {
  const token = readToken();
  const { visitorId } = metadata || {};
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/highlights/${visitorId}`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    const { data } = response || {};
    const { integrations = {}, highlights = [] } = data || {};
    const {
      hasHubspot = true,
      hasIntent = true,
      hasSalesforce = true,
      hasTechnology = true,
    } = integrations;
    let isHighlightsSetup = false;

    if (Array.isArray(highlights) && highlights.length !== 0) {
      isHighlightsSetup = true;
    }

    const highlightsFields = highlights.map(highlight => {
      const id = generateRandomId();
      return { ...highlight, id };
    });

    yield put(
      getHighlightsFields.success({
        data: {
          hasSalesforce,
          hasHubspot,
          hasIntent,
          hasTechnology,
          isHighlightsSetup,
          highlights: highlightsFields,
        },
      })
    );
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getHighlightsFields.error({ error }));
  }
}

export function* getHighlightsTemplatesSaga() {
  const token = readToken();
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: 'app/highlights/templates',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });

    const { data } = response || {};
    const { fields = {}, templates = [] } = data || {};
    const highlightsTemplates = templates?.map(({ template, ...rest }) => {
      const templates = template?.map(({ fields, ...rest }) => {
        const id = generateRandomId();
        return { ...rest, id, fields };
      });
      return { ...rest, template: templates };
    });
    const highlightsMenu = yield call(generateHighlightMenuList, fields);
    yield put(
      getHighlightsTemplates.success({ data: { highlightsMenu, templates: highlightsTemplates } })
    );
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getHighlightsTemplates.error({ error }));
  }
}

export function* saveHighlightsSaga({ metadata, payload }) {
  const token = readToken();
  const { isHighlightsSetup, closePopupHandler } = metadata;
  try {
    yield call(request, {
      method: 'post',
      endpoint: 'app/highlights',
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: {
          ...payload,
        },
      },
    });

    yield put(
      saveHighlightsBuilder.success({
        data: { template: [], isHighlightsSetup: !isHighlightsSetup },
      })
    );
    triggerNotification({
      title: isHighlightsSetup
        ? 'Highlights Successfully Setup'
        : 'Highlights Successfully Updated',
      type: NOTIFICATION.TYPE.SUCCESS,
    });
    closePopupHandler();
    const currentConversation = yield select(makeSelectCurrentConversation());
    yield put(getHighlightsFields.start({ metadata: { visitorId: currentConversation.user.id } }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(saveHighlightsBuilder.error({ error: errorMessage }));
  }
}

export function* getAttachmentsSaga() {
  const token = readToken();
  const { user } = yield select(makeSelectCurrentConversation());
  const { id: visitorId } = user || {};
  try {
    const response = yield call(request, {
      method: 'get',
      endpoint: `app/inbox/attachments/${visitorId}/1000`,
      config: {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    });
    const { data } = response || {};
    const visitorAttachments = generateFilesSchema(data?.visitorAttachments);
    const accountAttachments = generateFilesSchema(data?.accountAttachments);
    yield put(getAttachments.success({ data: { visitorAttachments, accountAttachments } }));
  } catch (error) {
    const errorMessage = convertErrorMsg(error);
    triggerNotification({
      title: errorMessage,
      type: NOTIFICATION.TYPE.ERROR,
    });
    yield put(getAttachments.error({ error }));
  }
}

export function* updateUploadProgressSaga({ metadata }) {
  const { currentConversationId, percentage, conversationType } = metadata;
  try {
    yield call(
      updateConversationLists,
      currentConversationId,
      percentage,
      UPDATE_CONVERSATION_LIST_TYPE.UPDATE_UPLOAD_PROGRESS,
      conversationType,
      null
    );
    yield put(updateUploadProgress.success({ data: { currentConversationId, percentage } }));
  } catch (error) {
    yield put(updateUploadProgress.error({ data: { currentConversationId } }));
  }
}

/**
 * Root saga manages watcher lifecycle
 */
export default function* chat() {
  yield takeLatest(GET_INBOX_DETAIL.START, getInboxDetails);
  yield takeEvery(GET_CONVERSATION_LIST.START, getConversationLists);
  yield takeLatest(GET_CONVERSATION_DETAIL.START, getConversationDetails);
  yield takeLatest(SEND_CONVERSATION_MESSAGES.START, sendMessages);
  yield takeLatest(START_BOOK_MEETING.START, startBooking);
  yield takeLatest(GET_TEAM_MEMBERS.START, getAgents);
  yield takeLatest(GET_CONVERSATION_MEMBERS.START, getParticipants);
  yield takeLatest(SET_ARCHIVE_STAR_CONVERSATION.START, setArchiveStarConversation);
  yield takeLatest(ADD_NEW_MEMBER_CONVERSATION.START, addNewMember);
  yield takeLatest(REMOVE_MEMBER_CONVERSATION.START, removeMember);
  yield takeLatest(READ_CONVERSATION.START, readConversation);
  yield takeLatest(UPDATE_CONVERSATION_STATUS.START, updateStatus);
  yield takeLatest(UPDATE_INBOX_DATA, updateData);
  yield takeLatest(GET_USER_STATUS.START, getUserStatusStart);
  yield takeLatest(UPDATE_USER_STATUS.START, updateUserStatusStart);
  yield takeLatest(GET_CONVERSATION_FROM_PARAMS.START, getConversationFromparamsStart);
  yield takeLatest(CLOSE_SELECTED_CONVERSATION.START, closeSelectedConversationList);
  yield takeLatest(SET_CONVERSATION_REQUEST.START, conversationRequest);
  yield takeLatest(ADD_TO_CONVERSATIONS_LIST, addToConversationList);
  yield takeLatest(REMOVE_FROM_CONVERSATIONS_LIST, removeFromConversationList);
  yield takeLatest(HANDLE_SENDING_MESSAGE_TO_PUSHER, handleSendingMessageToPusher);
  yield takeLatest(REMOVE_FROM_SENDING_MESSAGES, removeFromSendingMessagesStart);
  yield takeLatest(POLL_BACKEND, pollBackendStart);
  yield takeLatest(GET_CANNED_MESSAGES.START, getCannedMessagesStart);
  yield takeLatest(SAVE_CANNED_MESSAGE.START, saveCannedMessageStart);
  yield takeLatest(DELETE_CANNED_MESSAGE.START, deleteCannedMessageStart);
  yield takeEvery(SEND_MESSAGE, sendMessageStart);
  yield takeLatest(HAS_SEND_MESSAGE.START, hasSendMessageStart);
  yield takeLatest(CREATE_ZENDESK_TICKET.START, createZendeskTicketSaga);
  yield takeLatest(GET_KNOWLEDGE_BASE_ARTICLES.START, getKnowledgeBaseArticlesSaga);
  yield takeLatest(GET_CONNECTED_APPS.START, getConnectedAppsSaga);
  yield takeLatest(LOGOUT_USER.START, logoutUserStart);
  yield takeLatest(GET_SERVICE_CLOUD_ARTICLES.START, getServiceCloudArticlesSaga);
  yield takeLatest(CREATE_SERVICE_CLOUD_CASE.START, createServiceCloudCaseSaga);
  yield takeLatest(BLOCK_VISITOR.START, blockVisitorSaga);
  yield takeLatest(GET_TAGS.START, getTagsSaga);
  yield takeLatest(GET_SUBSCRIPTION_DETAILS.START, getSubscriptionDetailsSaga);
  yield takeLatest(GET_HIGHLIGHTS_FIELDS.START, getHighlightsFieldsSaga);
  yield takeLatest(GET_HIGHLIGHTS_TEMPLATES.START, getHighlightsTemplatesSaga);
  yield takeLatest(SAVE_HIGHLIGHTS_BUILDER.START, saveHighlightsSaga);
  yield takeLatest(GET_ATTACHMENTS.START, getAttachmentsSaga);
  yield takeLatest(UPDATE_UPLOAD_PROGRESS.START, updateUploadProgressSaga);
}
